import {Component, effect, inject, signal} from '@angular/core';
import {InvoiceGrid} from '../invoice-grid/invoice-grid';
import {InvoiceStateId, InvoiceSummary as InvoiceSummaryModel, PartyType} from '../../../../../api';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Router} from '@angular/router';
import {FormControl, FormGroup} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {FinancesPeriodStore} from '../../services/finances-period.store';
import {InvoiceStoreService} from '../../services/invoice-store.service';
import {InvoiceStore} from '../../services/invoice-store';
import {
  PeriodNavigatorComponent,
  PeriodRange,
  PeriodType
} from '../../../../components/period-navigator/period-navigator';
import {Search} from '../../../../components/search/search';
import {FilterContainer} from '../../../../components/filter-container/filter-container';
import {PartyDropdown} from '../../../../components/party-dropdown/party-dropdown';
import {LegalEntityDropdown} from '../legal-entity-dropdown/legal-entity-dropdown';
import {InvoiceStateDropdown} from '../invoice-state-dropdown/invoice-state-dropdown';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {CurrencyPipe} from '@angular/common';
import {ConfirmService} from '../../../../services/confirm.service';

@Component({
  selector: 'elcia-invoice-home',
  imports: [InvoiceGrid, ElciaButton, PeriodNavigatorComponent, Search, FilterContainer, PartyDropdown, LegalEntityDropdown, InvoiceStateDropdown, TranslatePipe],
  providers: [InvoiceStoreService, CurrencyPipe],
  templateUrl: './invoice-home.html',
  styleUrl: './invoice-home.scss'
})
export class InvoiceHome {
  protected periodStore = inject(FinancesPeriodStore);
  protected store = inject(InvoiceStoreService);
  protected invoiceStore = inject(InvoiceStore);
  private router = inject(Router);
  private confirmService = inject(ConfirmService);
  private translateService = inject(TranslateService);
  private currencyPipe = inject(CurrencyPipe);

  protected partyTypes = signal([PartyType.Contact, PartyType.Company]);
  protected isFilterDisplayed = signal(false);

  protected filterForm = new FormGroup({
    searchTerm: new FormControl('', {nonNullable: true}),
    customerIds: new FormControl<number[] | null>(null),
    legalEntityIds: new FormControl<number[] | null>(null),
    stateNames: new FormControl<InvoiceStateId[] | null>(null),
  });

  private readonly formValue = toSignal(
    this.filterForm.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.filterForm.value}
  );

  private readonly syncFilter = effect(() => {
    const form = this.formValue();
    this.store.filterParams.set({
      searchTerm: form.searchTerm || null,
      customerIds: form.customerIds ?? null,
      legalEntityIds: form.legalEntityIds ?? null,
      stateNames: form.stateNames ?? null,
    });
  });

  protected toggleFilterDisplayed() {
    this.isFilterDisplayed.update(v => !v);
  }

  protected runDailyInvoicing() {
    this.invoiceStore.runDailyInvoicingMutation.mutate();
  }

  protected setPeriod($event: PeriodRange) {
    this.periodStore.period.set($event);
  }

  protected navToForm() {
    this.router.navigate(['invoice', 'form']);
  }

  protected navToEdit(invoice: InvoiceSummaryModel) {
    this.router.navigate(['invoice', 'form', invoice.id]);
  }

  protected navToCredit(invoice: InvoiceSummaryModel) {
    this.router.navigate(['credit-note', 'form', invoice.id]);
  }

  // ConfirmService only translates the title, so the message is passed already translated.
  protected confirmDelete(invoice: InvoiceSummaryModel) {
    this.confirmService.confirm({
      title: 'entity.invoice',
      message: this.translateService.instant('invoice.delete-confirm', {
        customer: invoice.customerName,
        amount: this.currencyPipe.transform(invoice.amountWithoutTax, 'EUR'),
      }),
      accept: () => this.invoiceStore.deleteMutation.mutate(invoice.id),
    });
  }

  protected readonly PeriodType = PeriodType;
}
